import { defineStore } from 'pinia';
import api from '../utils/api';
import { DEFAULT_THEME, applyTheme, storeTheme } from '../utils/theme';

// Version: 2026-02-04-v2 - Deletions no longer reduce scan count
export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    subscription: null,
    settings: null,
    isFirstLogin: false,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => state.user !== null,
    userName: (state) => state.user?.name || '',
    userEmail: (state) => state.user?.email || '',
    hasActiveSubscription: (state) => 
      state.subscription?.status === 'active',
    enforceSubtotalValidation: (state) =>
      state.settings?.enforce_subtotal_validation ?? false,
    validateCompanyOnScan: (state) =>
      state.settings?.validate_company_on_scan ?? false,
    useWarehouse: (state) =>
      state.settings?.use_warehouse ?? false,
    useVendorCode: (state) =>
      state.settings?.use_vendor_code ?? false,
    useClients: (state) =>
      state.settings?.use_clients ?? false,
    clientsCompanyIds: (state) => {
      const ids = state.settings?.clients_company_ids;
      return Array.isArray(ids) ? ids : [];
    },
    useAccountingDate: (state) =>
      state.settings?.use_accounting_date ?? false,
    accountingDateMonthsBack: (state) =>
      state.settings?.accounting_date_months_back ?? 12,
    gasStationMultiInvoice: (state) =>
      state.settings?.gas_station_multi_invoice ?? false,
    // Whether to show the "VAT conflicts" warning (same VAT №, different name).
    // Defaults to true so existing users keep seeing it; can be turned off.
    validateVatConflicts: (state) =>
      state.settings?.validate_vat_conflicts ?? true,
    // Export credit-note amounts as negative (some accounting software requires it).
    creditNoteNegative: (state) =>
      state.settings?.credit_note_negative_amounts ?? false,
    // Apply the account code's pack-size factor to quantities in the Santa export
    // (a 24-bottle case billed as 1 leaves as 24 single items). Off by default so
    // existing exports are byte-identical until it is deliberately turned on.
    useQuantityMultiplier: (state) =>
      state.settings?.use_quantity_multiplier ?? false,
    // Ask for confirmation of the lines the Santa export books as a service
    // (internal code U) before the file is produced. Off by default: the export
    // runs exactly as before until it is deliberately turned on.
    confirmServiceLines: (state) =>
      state.settings?.confirm_service_lines ?? false,
    // The colour theme saved on this account, which may differ from the one
    // this device last painted.
    theme: (state) => state.settings?.theme ?? DEFAULT_THEME,
    scansRemaining: (state) => {
      if (!state.subscription) return 0;
      return state.subscription.scan_limit - state.subscription.scans_used;
    },
    usagePercentage: (state) => {
      if (!state.subscription || state.subscription.scan_limit === 0) return 0;
      return Math.round((state.subscription.scans_used / state.subscription.scan_limit) * 100);
    },
  },

  actions: {
    async fetchUser() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get('/api/me');
        this.user = response.data.user;
        this.subscription = response.data.subscription;
        this.settings = response.data.settings;
        this.isFirstLogin = Boolean(response.data.is_first_login);

        // The account's theme wins over whatever this device remembered, so
        // the choice follows the person to another browser. Stored too, so the
        // next boot paints it before /api/me has answered.
        storeTheme(applyTheme(this.settings?.theme));

        console.log('✅ fetchUser() completed. Subscription data:', {
          scans_used: this.subscription?.scans_used,
          scan_limit: this.subscription?.scan_limit,
          hard_scans_used: this.subscription?.hard_scans_used,
          hard_scan_limit: this.subscription?.hard_scan_limit
        });
      } catch (error) {
        console.error('Failed to fetch user:', error);
        this.error = error.message;
        // User is not authenticated
        this.user = null;
        this.subscription = null;
        this.settings = null;
        
        // If we get a 401, we're not authenticated - redirect to login
        if (error.response && error.response.status === 401) {
          // Don't redirect here, let Laravel handle it
          // window.location.href = '/login';
        }
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      try {
        await api.post('/api/logout');
        this.user = null;
        this.subscription = null;
        this.settings = null;
        window.location.href = '/login';
      } catch (error) {
        console.error('Logout failed:', error);
        // Force redirect anyway
        window.location.href = '/login';
      }
    },

    async updateSettings(settings) {
      try {
        await api.post('/api/companies/bulk-update-settings', settings);
        this.settings = { ...this.settings, ...settings };
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.message || error.message };
      }
    },

    async updateProfile(data) {
      try {
        const response = await api.put('/profile', data);
        this.user = { ...this.user, ...response.data.user };
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.message || error.message };
      }
    },

    async refreshSubscription() {
      try {
        console.log('🔄 refreshSubscription() called...');
        const response = await api.get('/api/me');
        this.user = response.data.user;
        this.subscription = response.data.subscription;
        this.settings = response.data.settings;
        console.log('✅ refreshSubscription() completed. New scans_used:', this.subscription?.scans_used);
      } catch (error) {
        console.error('Failed to refresh subscription:', error);
      }
    },
  },
});
